import { dbApiUrl } from './dbApi';

export class AuthApiError extends Error {
  constructor(message, code = null) {
    super(message);
    this.name = 'AuthApiError';
    this.code = code;
  }
}

export function isBlacklistedAuthError(error) {
  if (error instanceof AuthApiError) {
    return error.code === 'blacklisted';
  }
  const message = error instanceof Error ? error.message : String(error);
  return /blocked|blacklist/i.test(message);
}

async function jsonFromResponse(res) {
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new AuthApiError(data?.error || `HTTP ${res.status}`, data?.error_code || null);
  }
  return data;
}

export async function authCheck() {
  const res = await fetch(dbApiUrl('api/auth/check_auth.php'), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store'
  });
  return jsonFromResponse(res);
}

export async function authLogin({ username, password, remember = false }) {
  const res = await fetch(dbApiUrl('api/auth/login.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ username, password, remember: !!remember })
  });
  return jsonFromResponse(res);
}

export async function authGoogleLogin({ idToken, remember = true }) {
  const res = await fetch(dbApiUrl('api/auth/google_login.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ id_token: idToken, remember: !!remember })
  });
  return jsonFromResponse(res);
}

export async function authLogout() {
  const res = await fetch(dbApiUrl('api/auth/logout.php'), {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store'
  });
  return jsonFromResponse(res);
}

export async function authMe() {
  const res = await fetch(dbApiUrl('api/auth/me.php'), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store'
  });
  return jsonFromResponse(res);
}

export async function authUpdateMe(body) {
  const res = await fetch(dbApiUrl('api/auth/me_update.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

export async function authUpdatePersonal(body) {
  const res = await fetch(dbApiUrl('api/auth/me_personal_update.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

export async function authAdminUsers({ page = 1, perPage = 10, search = '', role = '', tier = '', cycle = '', status = '' } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage)
  });
  const q = String(search || '').trim();
  if (q) params.set('search', q);
  if (role) params.set('role', role);
  if (tier) params.set('tier', tier);
  if (cycle) params.set('cycle', cycle);
  if (status) params.set('status', status);
  const res = await fetch(dbApiUrl(`api/auth/admin_users.php?${params}`), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store'
  });
  return jsonFromResponse(res);
}

export async function authAdminSaveUser(body) {
  const res = await fetch(dbApiUrl('api/auth/admin_users.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ action: 'save', ...(body || {}) })
  });
  return jsonFromResponse(res);
}

export async function authAdminUserAction({ id, action }) {
  const res = await fetch(dbApiUrl('api/auth/admin_users.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ id, action })
  });
  return jsonFromResponse(res);
}

export async function authCompleteProfile(body) {
  const res = await fetch(dbApiUrl('api/auth/complete_profile.php'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body || {})
  });
  return jsonFromResponse(res);
}

